import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IonAlert, IonButton, IonDatetime, IonInput, IonItem, IonLabel, IonList, IonSelect, IonSelectOption, IonToast, IonModal, IonTextarea, IonIcon, IonChip } from '@ionic/react';
import { handleErrors } from '../services/common.service';
import { useLocation } from 'react-router-dom';
import useNavigation from '../hooks/useNavigation';
import { addLead, getLead, getLeadConfig, updateLead } from '../services/lead.service';
import { useRecoilValue } from 'recoil';
import { userProfileState } from '../services/state.service';
import UserList from '../pages/UserList';
import { GeolocationService, getReverseGecodingAddress } from '../services/geolocation.service';
import { locationOutline, locationSharp } from 'ionicons/icons';
import SelectItem from './SelectItem';
import countryCodes from '../country_code.json';
import './CreateLead.css';

const CreateLead = () => {
    const navigateTo = useNavigation();
    const location = useLocation();
    const [lead, setLead] = useState();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [onSuccess, setOnSuccess] = useState('');
    const [onError, setOnError] = useState('');
    const userProfile: any = useRecoilValue(userProfileState);
    const [showSelectUserModal, setShowSelectUserModal] = useState(false);
    const [showSelectCountryCodeModal, setShowSelectCountryCodeModal] = useState(false);
    
    // GPS and Location related states
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [gpsEnabled, setGpsEnabled] = useState<boolean | null>(null);
    const [locationError, setLocationError] = useState<string>('');
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const [followUpDate, setFollowUpDate] = useState<Date>(tomorrow);

    const compareWith = (o1: any, o2: any) => {
        if (Array.isArray(o2)) {
            return o2.indexOf(o1) !== -1;
        }
        return o1 === o2;
    };

    // Check GPS status and location permission
    const checkGPSStatus = async () => {
        try {
            const geoService = new GeolocationService();
            
            // Check if GPS is enabled
            const isGPSEnabled = await geoService.isGPSEnabled();
            setGpsEnabled(isGPSEnabled);
            
            if (!isGPSEnabled) {
                setLocationError('GPS is not enabled or location access denied');
                return;
            }
            
            await getCurrentLocation();
        } catch (error: any) {
            setGpsEnabled(false);
            setLocationError('Failed to check GPS status: ' + (error.message || 'Unknown error'));
        }
    };

    // Get current location with reverse geocoding
    const getCurrentLocation = async () => {
        setIsGettingLocation(true);
        setLocationError('');
        
        try {
            const geoService = new GeolocationService();
            const position = await geoService.getCurrentPosition();
            
            if (!position?.coords) {
                throw new Error('Failed to get location coordinates');
            }

            const { latitude, longitude } = position.coords;
                        
            // Get reverse geocoding data
            try {
                const reverseGeocodeData = await getReverseGecodingAddress(latitude, longitude);
                if (reverseGeocodeData && reverseGeocodeData.display_name) {
                    const address = reverseGeocodeData.display_name;
                    formik.setFieldValue('location', address);
                    
                    // Set taluka, district, and state from reverse geocoding data
                    if (reverseGeocodeData.address) {
                        formik.setFieldValue('taluka', reverseGeocodeData.address.subDistrict || '');
                        formik.setFieldValue('district', reverseGeocodeData.address.district || '');
                        formik.setFieldValue('state', reverseGeocodeData.address.state || '');
                    }
                }
            } catch (geocodeError) {
                console.error('Reverse geocoding failed:', geocodeError);
                formik.setFieldValue('location', `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
            }

            setGpsEnabled(true);
            setLocationError(''); // Clear any previous errors
            
        } catch (error: any) {
            console.log('Location error:', error);
            
            // Don't immediately disable GPS on timeout
            if (error.code === 1) {
                setLocationError('Location permission denied. Please enable location access.');
                setGpsEnabled(false);
            } else if (error.code === 2) {
                setLocationError('Location unavailable. Please check your GPS settings.');
                setGpsEnabled(false);
            } else if (error.code === 3) {
                // For timeout, just show a warning but don't disable GPS
                setLocationError('GPS signal weak. Please move to an open area or try again.');
                // Keep GPS enabled but show warning
            } else {
                setLocationError('Failed to get location. Please check your GPS settings.');
                setGpsEnabled(false);
            }
        } finally {
            setIsGettingLocation(false);
        }
    };

    useEffect(() => {
        checkGPSStatus();
    }, [gpsEnabled]);

    function setValues(data: any) {
        if (data) {
            formik.setValues({
                edit: true,
                date: data.date,
                type: data.type,
                source: data.source,
                contactName: data.contactName,
                contactPhoneNumber: data.contactPhoneNumber,
                countryCode: data.countryCode || '',
                location: data.location,
                response: data.response,
                followUpDate: data.followUpDate,
                status: data.status,
                account: data.account,
                details: data.details,
                servicedBy: data.servicer?.farmUserId || data.servicedBy,
                comment: data.comment,
                taluka: data.taluka || '',
                district: data.district || '',
                state: data.state || ''
            });
        }
        setLead(data);
    }

    useEffect(() => {
        let mounted = true;
        const id: any = (location.state as any)?.leadId ?? null;
        if (id) {
            getLead(id)
                .then(user => {
                    if (mounted) {
                        setValues(user);
                    }
                });
        } else {
            formik.setFieldValue('contactName', (location.state as any)?.contactName ?? '');
            formik.setFieldValue('contactPhoneNumber', (location.state as any)?.contactPhoneNumber ?? '');
            formik.setFieldValue('countryCode', (location.state as any)?.countryCode ?? '+91');
            formik.setFieldValue('location', (location.state as any)?.location ?? '');
            formik.setFieldValue('servicedBy', (location.state as any)?.location ?? userProfile.farmUserId);
            formik.setFieldValue('comment', (location.state as any)?.comment ?? '')
            formik.setFieldValue('status', (location.state as any)?.status ?? 'new')
            formik.setFieldValue('followUpDate', (location.state as any)?.followUpDate ?? followUpDate.toISOString())
            formik.setFieldValue('taluka', (location.state as any)?.taluka ?? '');
            formik.setFieldValue('district', (location.state as any)?.district ?? '');
            formik.setFieldValue('state', (location.state as any)?.state ?? '');
        }
        return () => { mounted = false };
    }, [location]);

    const formik = useFormik({
        initialValues: {
            edit: false,
            date: new Date().toISOString(),
            type: '',
            source: '',
            contactName: '',
            contactPhoneNumber: '',
            countryCode: '+91',
            location: '',
            response: '',
            followUpDate: '',
            status: 'Open',
            account: 'lead',
            details: '',
            servicedBy: '',
            comment: '',
            taluka: '',
            district: '',
            state: ''
        },
        validationSchema: Yup.object({
            // date: Yup.string().trim().required('Required'),
            // type: Yup.string().trim().required('Required'),
            // source: Yup.string().trim().required('Required'),
            // contactName: Yup.string().trim().required('Required'),
            // contactPhoneNumber: Yup.string().trim().required('Required'),
            // location: Yup.string().trim().required('Required'),
            // response: Yup.string().trim().required('Required'),
            // status: Yup.string().trim().required('Required'),
            servicedBy: Yup.string().trim().required('Required'),
            location: Yup.string().trim().required('Location is required. Please enable GPS to get your location.'),
            contactName: Yup.string().trim().required('Contact name is required'),
            contactPhoneNumber: Yup.string().trim().required('Phone number is required'),
        }),
        onSubmit: values => {
            setShowConfirmation(true);
        },
    });

    const [leadTypes, setLeadTypes] = useState([] as any[]);
    const [sources, setSources] = useState([] as any[]);
    const [responseTypes, setResponseTypes] = useState([] as any[]);
    const [statuses, setStatuses] = useState([] as any[]);

    useEffect(() => {
        let mounted = true;
        getLeadConfig()
            .then(response => {
                if (mounted) {
                    setLeadTypes(response.type);
                    setSources(response.source);
                    setResponseTypes(response.response);
                    setStatuses(response.status);
                }
            })
        return () => { mounted = false };
    }, [])


    function setServicedById(item: any) {
        formik.setFieldValue('servicedBy', item.farmUserId);
        setShowSelectUserModal(false);
    }

    function setCountryCode(item: any) {
        formik.setFieldValue('countryCode', item.dial_code);
        setShowSelectCountryCodeModal(false);
    }

    // Function to clean phone number by removing spaces, dashes, and other non-numeric characters
    const cleanPhoneNumber = (value: string): string => {
        if (!value) return '';
        // Remove spaces, dashes, parentheses, and other non-numeric characters except digits
        return value.replace(/[^\d]/g, '');
    }

    const onContactNumberChange = (value: string) => {
        const cleanedNumber = cleanPhoneNumber(value);
        formik.setFieldValue('contactPhoneNumber', cleanedNumber);
    }

    const saveLead = async (values: any) => {
        try {
            delete values.edit;
            delete values.id;
            delete values.servicer;
            values.servicedBy =  values.servicedBy || userProfile.username; 
            const data = Object.fromEntries(Object.entries(values).filter(([_, v]) => !!v));
            if (!lead) {
                const response = await addLead(data).then(handleErrors);
                setOnSuccess('Lead details added');
            } else {
                delete (lead as any).servicer;
                const updatedObj = Object.assign({}, lead, data);
                const response = await updateLead(updatedObj).then(handleErrors);
                setOnSuccess('Lead details updated');
            }
        } catch (err: any) {
            let errorMessage = 'Mobile number already exists';
            setOnError(errorMessage);
        }
    };




    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <IonList>

                    <IonItem>
                        <IonLabel position="stacked">Contact Name</IonLabel>
                        <IonInput id="contactName" name="contactName" value={formik.values.contactName} placeholder="Contact Name" onIonChange={formik.handleChange}></IonInput>
                    </IonItem>
                    {formik.touched.contactName && formik.errors.contactName ? (<div className="errorMsg">{formik.errors.contactName}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Country Code</IonLabel>
                        <IonInput
                            id="countryCode"
                            name="countryCode"
                            readonly
                            value={formik.values.countryCode}
                            placeholder="Country Code"
                            onClick={(e) => setShowSelectCountryCodeModal(true)}
                        ></IonInput>
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Mobile Number</IonLabel>
                        <IonInput
                            id="contactPhoneNumber"
                            name="contactPhoneNumber"
                            type="tel"
                            inputmode="tel"
                            value={formik.values.contactPhoneNumber}
                            placeholder="Mobile No. of the Contact"
                            onIonChange={(e) => onContactNumberChange(e.detail.value || '')}
                        ></IonInput>
                    </IonItem>
                    {formik.touched.contactPhoneNumber && formik.errors.contactPhoneNumber ? (<div className="errorMsg">{formik.errors.contactPhoneNumber}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Follow-up Date</IonLabel>
                        <IonDatetime id="followUpDate" name="followUpDate" displayFormat="DD/MM/YYYY" placeholder="Follow-up Date" value={formik.values.followUpDate} onIonChange={formik.handleChange}></IonDatetime>
                    </IonItem>
                    <IonItem>
                        <IonLabel position="stacked">Comment</IonLabel>
                        <IonTextarea id="comment" name="comment" value={formik.values.comment} placeholder="Comment" onIonChange={formik.handleChange}></IonTextarea>
                    </IonItem>
                    {formik.touched.comment && formik.errors.comment ? (<div className="errorMsg">{formik.errors.comment}</div>) : null}


                    <IonItem>
                        <IonLabel position="stacked">Status</IonLabel>
                        <IonSelect id="status" name="status" compareWith={compareWith} value={formik.values.status} placeholder="Select Status" onIonChange={formik.handleChange}>
                            {statuses && statuses.map(item => {
                                return <IonSelectOption key={item.id} value={item.id}>{item.value}</IonSelectOption>;
                            })}
                        </IonSelect>
                    </IonItem>
                    {formik.touched.status && formik.errors.status ? (<div className="errorMsg">{formik.errors.status}</div>) : null}

                    {/* GPS Status Indicator */}
                    <IonItem lines="none">
                        <IonChip 
                            color={gpsEnabled ? 'success' : gpsEnabled === false ? 'danger' : 'warning'}
                            outline
                        >
                            <IonIcon icon={gpsEnabled ? locationSharp : locationOutline} />
                            <IonLabel>
                                {gpsEnabled ? 'GPS Enabled' : gpsEnabled === false ? 'GPS Disabled' : 'Checking GPS...'}
                            </IonLabel>
                        </IonChip>
                    </IonItem>

                    <IonItem>
                        <IonLabel position="stacked">Location</IonLabel>
                        <IonInput 
                            disabled={true} 
                            id="location" 
                            name="location" 
                            value={formik.values.location} 
                            placeholder="Location will be set automatically from GPS" 
                            onIonChange={formik.handleChange}
                            className="location-input"
                        ></IonInput>
                    </IonItem>
                    {formik.touched.location && formik.errors.location ? (<div className="errorMsg">{formik.errors.location}</div>) : null}


                    {/*    <IonItem>
                        <IonLabel position="stacked">Date</IonLabel>
                        <IonDatetime id="date" name="date" displayFormat="DD/MM/YYYY" placeholder="Date" value={formik.values.date} onIonChange={formik.handleChange}></IonDatetime>
                    </IonItem>
                    {formik.touched.date && formik.errors.date ? (<div className="errorMsg">{formik.errors.date}</div>) : null}

                                      <IonItem>
                        <IonLabel position="stacked">Type</IonLabel>
                        <IonSelect id="type" name="type" compareWith={compareWith} value={formik.values.type} placeholder="Select Type" onIonChange={formik.handleChange}>
                            {leadTypes && leadTypes.map(item => {
                                return <IonSelectOption key={item.id} value={item.id}>{item.value}</IonSelectOption>;
                            })}
                        </IonSelect>
                    </IonItem>
                    {formik.touched.type && formik.errors.type ? (<div className="errorMsg">{formik.errors.type}</div>) : null}

                    <IonItem>
                        <IonLabel position="stacked">Source</IonLabel>
                        <IonSelect id="source" name="source" value={formik.values.source} compareWith={compareWith} placeholder="Select Source" onIonChange={formik.handleChange}>
                            {sources && sources.map(item => {
                                return <IonSelectOption key={item.id} value={item.id}>{item.value}</IonSelectOption>;
                            })}
                        </IonSelect>
                    </IonItem>
                    {formik.touched.source && formik.errors.source ? (<div className="errorMsg">{formik.errors.source}</div>) : null}


                    <IonItem>
                        <IonLabel position="stacked">Response</IonLabel>
                        <IonSelect id="response" name="response" value={formik.values.response} compareWith={compareWith} placeholder="Select Response" onIonChange={formik.handleChange}>
                            {responseTypes && responseTypes.map(item => {
                                return <IonSelectOption key={item.id} value={item.id}>{item.value}</IonSelectOption>;
                            })}
                        </IonSelect>
                    </IonItem>
                    {formik.touched.response && formik.errors.response ? (<div className="errorMsg">{formik.errors.response}</div>) : null}


                    <IonItem>
                        <IonLabel position="stacked">Details</IonLabel>
                        <IonTextarea id="details" name="details" value={formik.values.details} placeholder="Lead Details" onIonChange={formik.handleChange}></IonTextarea>
                    </IonItem>*/}

                    <IonItem>
                        <IonLabel position="stacked">Serviced By</IonLabel>
                        <IonInput readonly id="servicedBy" name="servicedBy" value={formik.values.servicedBy} placeholder="Select Servicer" onClick={e => {setShowSelectUserModal(true);}}></IonInput>
                    </IonItem>
                    {formik.touched.servicedBy && formik.errors.servicedBy ? (<div className="errorMsg">{formik.errors.servicedBy}</div>) : null}

                </IonList>

                <IonButton 
                    expand="block" 
                    type="submit"
                    disabled={!formik.values.location || isGettingLocation}
                >
                    {(!lead ? 'Create' : 'Update') + ' Lead'}
                </IonButton>
            </form>
            <IonAlert
                isOpen={showConfirmation}
                onDidDismiss={() => setShowConfirmation(false)}
                header={'Confirm!'}
                message={'Please make sure that you have verified all the lead details. Save Lead?'}
                buttons={[
                    {
                        text: 'No',
                        role: 'cancel',
                        cssClass: 'secondary'
                    },
                    {
                        text: 'Yes',
                        handler: () => {
                            saveLead(formik.values);
                        }
                    }
                ]}
            />

            {showSelectUserModal && <IonModal isOpen={showSelectUserModal} >
            <UserList
                    selectedValue={formik.values.servicedBy}
                    onClickHandler={setServicedById}
                    onClose={setShowSelectUserModal}
                />
            </IonModal>}

            {showSelectCountryCodeModal && (
                <IonModal isOpen={showSelectCountryCodeModal}>
                    <SelectItem
                        label="Select Country"
                        items={countryCodes}
                        labelKey="name"
                        valueKey="dial_code"
                        setItem={setCountryCode}
                        selectedValue=""
                        onClose={setShowSelectCountryCodeModal}
                    />
                </IonModal>
            )}

            <IonToast
                isOpen={!!onSuccess}
                onDidDismiss={() => setOnSuccess('')}
                message={onSuccess}
                duration={2000}
                color="success"
            />

            <IonToast
                isOpen={!!onError}
                onDidDismiss={() => setOnError('')}
                message={onError}
                duration={5000}
                color="danger"
                buttons={[
                    {
                        text: 'OK',
                        role: 'cancel'
                    }
                ]}
            />

        </>
    );
};

export default CreateLead;